import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect } from 'react';
import { db } from './firebase';
import { adminWriteConfig, ConfigWritePatch } from './api';
import { validateConfigPatch } from './configSchemas';

export function useConfigDoc<T = Record<string, unknown>>(docId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['config', docId];

  // Live updates via onSnapshot
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', docId), (snap) => {
      queryClient.setQueryData(queryKey, snap.exists() ? snap.data() : {});
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  return useQuery<T>({
    queryKey,
    queryFn: async () => {
      // Initial fetch happens via the onSnapshot above; this returns whatever
      // is currently cached.
      return (queryClient.getQueryData(queryKey) as T) || ({} as T);
    },
    staleTime: Infinity,
  });
}

export function useWriteConfig(docId: string) {
  return useMutation({
    mutationFn: async (patch: ConfigWritePatch) => {
      // Reject locally before round-tripping to Cloud Functions so admins
      // see typo / range errors immediately and we don't waste a write.
      const validated = validateConfigPatch(docId, patch);
      const res = await adminWriteConfig({ docId, patch: validated });
      return res.data;
    },
  });
}
